<template>
  <EquipmentRow :key="`${intake?.id}-${index}`" data-cy="equipment-row">
    <template #left></template>
    <template #default="{ toggle }">
      <div class="flex flex-column gap-2 intake-row-content">
        <AddRemoveButtons
          :disable-add-above="disableAddAbove"
          :disable-add-below="disableAddBelow"
          :disable-remove="disableRemove"
          :hide-add-above="hideAddAbove"
          :hide-add-below="hideAddBelow"
          :hide-remove="hideRemove"
          :data-cy="`add-remove-buttons`"
          class="intake-add-remove-buttons"
          @add-above="$emit('add-above')"
          @add-below="$emit('add-below')"
          @remove="$emit('remove', intake)"
        >
          <template #middle-content>
            <EquipmentPanel
              class="flex-grow-1 w-full intake-panel"
              :rule-names="[
                'intake_size_top_casing',
                'intake_size_bypass_tubing_top_casing',
                'intake_size_bypass_tubing_bottom_casing',
                'intake_size_bottom_casing',
                'intake_shaft_limit_std',
                'intake_obsolete',
                'gas_separation_max_flow_exceeded',
                'intake_use_natural_gas_separation'
              ]"
              :xref-id="intake?.id"
              :stack="stack"
              data-cy="equipment-panel"
              @toggle="toggle"
            >
              <template #header>Intake Type</template>
              <template v-if="intake?.r_intake" #trigger>
                {{
                  computeIntakeLabel(
                    intake?.r_intake,
                    intake?.use_high_strength_shaft
                  )
                }}
              </template>
            </EquipmentPanel>
          </template>
        </AddRemoveButtons>
        <!-- Split view: Checkbox aligned to left -->
        <div v-if="isSplitView" class="shaft-row w-full" data-cy="shaft-row">
          <div class="field-checkbox align-items-center flex shaft-checkbox">
            <AlertWrapper
              :rule-names="['intake_shaft_limit_std']"
              :xref-id="intake?.id"
              :stack="stack"
            >
              <Checkbox
                :model-value="intake?.use_high_strength_shaft || false"
                :disabled="!intake?.r_intake || isScenarioReadonly"
                :input-id="`high-strength-shaft-${intake?.id}`"
                binary
                data-cy="checkbox-high-strength-shaft"
                @update:model-value="
                  $emit('update:intakes', {
                    ...intake,
                    use_high_strength_shaft: $event
                  })
                "
              />
              <label
                v-tooltip.top="'Use High Strength Shaft'"
                :for="`high-strength-shaft-${intake?.id}`"
                :class="{ disabled: !intake?.r_intake }"
                class="shaft-label"
              >
                <span class="label-full"> Use High Strength Shaft</span>
                <span class="label-compact"> Use High Strength Shaft</span>
              </label>
            </AlertWrapper>
          </div>
        </div>
        <!-- Default view: Checkbox (original structure) -->
        <div
          v-else
          class="flex align-items-center gap-2 shaft-row"
          data-cy="shaft-row"
        >
          <div class="field-checkbox align-items-center flex shaft-checkbox">
            <AlertWrapper
              :rule-names="['intake_shaft_limit_std']"
              :xref-id="intake?.id"
              :stack="stack"
            >
              <Checkbox
                :model-value="intake?.use_high_strength_shaft || false"
                :disabled="!intake?.r_intake || isScenarioReadonly"
                :input-id="`high-strength-shaft-${intake?.id}`"
                binary
                data-cy="checkbox-high-strength-shaft"
                @update:model-value="
                  $emit('update:intakes', {
                    ...intake,
                    use_high_strength_shaft: $event
                  })
                "
              />
              <label
                v-tooltip.top="'Use High Strength Shaft'"
                :for="`high-strength-shaft-${intake?.id}`"
                :class="{ disabled: !intake?.r_intake }"
                class="shaft-label"
              >
                <span class="label-full">Use High Strength Shaft</span>
                <span class="label-compact">Use High Strength Shaft</span>
              </label>
            </AlertWrapper>
          </div>
        </div>
        <!-- Split view: Length and Weight in separate rows -->
        <div
          v-if="isSplitView"
          class="flex align-items-center gap-2 flex-wrap"
          data-cy="intake-details-row"
        >
          <div class="mr-2 flex-1">
            <ConverterInput
              label="Length"
              :model-value="intake?.r_intake?.length_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit()"
              :model="lengthUnitModel"
              mode="convert"
              data-cy="converter-input-length"
            />
          </div>
          <div class="mr-2 flex-1">
            <ConverterInput
              label="Weight"
              :model-value="intake?.r_intake?.weight_pounds"
              :base-unit="MassUnits.Pounds"
              :target-unit="getMassUnit()"
              :model="massUnitModel"
              mode="convert"
              data-cy="converter-input-weight"
            />
          </div>
        </div>
        <!-- Default view: Length and Weight below intake type -->
        <div
          v-if="!isSplitView"
          class="flex align-items-center gap-2 flex-wrap"
          data-cy="intake-details-row"
        >
          <ConverterInput
            label="Length"
            :model-value="intake?.r_intake?.length_inches"
            :base-unit="LengthUnits.Inches"
            :target-unit="getLargeLengthUnit()"
            :model="lengthUnitModel"
            mode="convert"
            data-cy="converter-input-length"
          />
          <ConverterInput
            label="Weight"
            :model-value="intake?.r_intake?.weight_pounds"
            :base-unit="MassUnits.Pounds"
            :target-unit="getMassUnit()"
            :model="massUnitModel"
            mode="convert"
            data-cy="converter-input-weight"
          />
        </div>
      </div>
    </template>
    <template #expand="{ toggle }">
      <IntakeSearchView
        :is-admin="false"
        data-cy="intake-search-view"
        @select="handleIntakeSelect(intake, $event, toggle)"
      />
    </template>
    <!-- Empty right slot for non-split view since length/weight are now below -->
    <template v-if="!isSplitView" #right></template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import IntakeSearchView from "@/components/equipment/intakes/IntakeSearchView.vue";
import AlertWrapper from "@/components/AlertWrapper.vue";
import Checkbox from "primevue/checkbox";
import type { XrefIntakes } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  scenarioFormKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import type { Intakes } from "gql/api";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const scenarioForm = injectStrict(scenarioFormKey, null);
const { isScenarioReadonly } = useEditPermissions(undefined, scenarioForm);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit, getMassUnit } = useScenarioUnits(scenario, sizing);

const lengthUnitModel = LargeLengthUnits.options();
const massUnitModel = MassUnits.options();

const props = defineProps({
  intake: {
    type: Object as PropType<XrefIntakes[number]>,
    default: null
  },
  index: {
    type: Number,
    default: undefined
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

const isSplitView = computed(() => !!props.stack);

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", intake: XrefIntakes[number]): void;
  (e: "update:intakes", intake: XrefIntakes[number]): void;
}>();

provide(
  "selected-equipment-id",
  computed(() => props.intake?.r_intake?.id)
);

function handleIntakeSelect(
  xrefIntake: XrefIntakes[number],
  selectedIntakeEquipment: Intakes,
  toggle: () => void
) {
  toggle();
  const isHps =
    scenarioForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps ||
    sizing?.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  $emit("update:intakes", {
    ...xrefIntake,
    id: xrefIntake?.id,
    intake_id: selectedIntakeEquipment?.id,
    r_intake: selectedIntakeEquipment,
    use_high_strength_shaft: isHps
      ? xrefIntake.use_high_strength_shaft ?? true
      : xrefIntake.use_high_strength_shaft
  });
}
</script>

<style lang="scss" scoped>
// Note: All compact-mode styles have been moved to compact_mode.scss
// This file only contains non-compact-mode specific styles

// Full mode - show full label (only applies when NOT in compact mode)
.label-full {
  display: inline;
}

.label-compact {
  display: none;
}

// Non-split view: Make intake type expand to full width
:deep(.grid.mb-2) {
  // Middle column (intake type panel) - take full width when right slot is empty
  > .col {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  // Right column should be empty/hidden for non-split view
  > div:not(.col):not(.col-12) {
    display: none;
  }
}

.intake-row-content {
  width: 100%;
  min-width: 0;
}

.intake-panel {
  min-width: 0;
  width: 100%;
  flex: 1 1 auto;
}

// Add padding between intake type and high strength shaft checkbox
.shaft-row {
  margin-top: 0.75rem;
}
</style>
